import { sync } from "@/core/sync";
import { BINGO_EVENTS, CORE_EVENTS, makeEvent } from "@/core/sync/events";
import { useHotelStore } from "@/core/state/hotelStore";
import { useSettingsStore } from "@/core/state/settingsStore";
import { useBingoSettingsStore } from "@/plugins/bingo/state/bingoSettingsStore";
import {
  GameStatus,
  Stage,
  useBingoGameStore,
} from "@/plugins/bingo/state/bingoGameStore";

/**
 * projectorReceiver
 *
 * Runs in the Projector window. Subscribes to sync events and mutates
 * the local Zustand store DIRECTLY (via setState). It never publishes,
 * so there is no echo loop with the Host.
 *
 * On mount, publishes a STATE_REQUEST so a late-joining Projector
 * (e.g. window opened mid-game) can hydrate immediately.
 */

const set = useBingoGameStore.setState;
const get = useBingoGameStore.getState;

let transitionTimer = null;

function scheduleTransitionToInProgress() {
  if (transitionTimer) clearTimeout(transitionTimer);
  transitionTimer = setTimeout(() => {
    if (get().status === GameStatus.PRE_GAME_TRANSITION) {
      set({ status: GameStatus.IN_PROGRESS });
    }
    transitionTimer = null;
  }, 2500);
}

function handle(event) {
  if (!event) return;

  // Cross-plugin core events
  if (event.pluginId === "core") {
    if (event.type === CORE_EVENTS.MUTE_TOGGLED) {
      useSettingsStore.setState((s) => ({
        settings: { ...s.settings, muted: !!event.payload.muted },
      }));
    } else if (event.type === CORE_EVENTS.HOTEL_BRANDING_UPDATED) {
      // Projector's own store is the receiver — apply the patch silently
      // (opts.silent skips re-broadcast and skips backend save).
      useHotelStore.getState().setHotel(event.payload || {}, { silent: true });
    } else if (event.type === CORE_EVENTS.SETTINGS_UPDATED) {
      useSettingsStore
        .getState()
        .setSettings(event.payload || {}, { silent: true });
    }
    return;
  }

  if (event.pluginId !== "bingo") return;

  // Bingo plugin settings live-sync (reveal timing, toggles).
  if (event.type === BINGO_EVENTS.SETTINGS_UPDATED) {
    useBingoSettingsStore.getState().applyRemotePatch(event.payload || {});
    return;
  }

  switch (event.type) {
    case BINGO_EVENTS.NEW_GAME: {
      set({
        sessionId: event.payload.sessionId,
        status: GameStatus.PRE_GAME_TRANSITION,
        stage: Stage.ONE_LINE,
        drawSequence: [],
        drawIndex: 0,
        called: [],
        undoDepth: 0,
        manualMarks: [],
        winners: {
          one_line: { name: null, announcedAt: null },
          two_lines: { name: null, announcedAt: null },
          full_house: { name: null, announcedAt: null },
        },
        pendingWinnerName: "",
        mode: event.payload.mode || "manual",
        autoInterval: event.payload.autoInterval || 5,
      });
      scheduleTransitionToInProgress();
      break;
    }
    case BINGO_EVENTS.NUMBER_CALLED: {
      set((s) => ({
        drawIndex: event.payload.drawIndex ?? s.drawIndex,
        undoDepth: event.payload.undoDepth ?? 0,
        called: [
          ...s.called,
          { value: event.payload.value, calledAt: new Date(event.ts).toISOString() },
        ],
        status: GameStatus.IN_PROGRESS,
      }));
      break;
    }
    case BINGO_EVENTS.NUMBER_UNDONE: {
      set((s) => ({
        drawIndex: event.payload.drawIndex ?? s.drawIndex,
        undoDepth: event.payload.undoDepth ?? s.undoDepth,
        called: s.called.slice(0, -1),
        status: GameStatus.PAUSED,
      }));
      break;
    }
    case BINGO_EVENTS.PAUSED:
      set({ status: GameStatus.PAUSED });
      break;
    case BINGO_EVENTS.RESUMED:
      set({ status: GameStatus.IN_PROGRESS });
      break;
    case BINGO_EVENTS.MODE_CHANGED:
      set({ mode: event.payload.mode });
      break;
    case BINGO_EVENTS.INTERVAL_CHANGED:
      set({ autoInterval: event.payload.autoInterval });
      break;
    case BINGO_EVENTS.STAGE_CHANGED:
      set({ stage: event.payload.stage });
      break;
    case BINGO_EVENTS.WINNER_ANNOUNCED: {
      const { stage, name, at } = event.payload;
      set((s) => ({
        status: GameStatus.ANNOUNCING_WINNER,
        winners: {
          ...s.winners,
          [stage]: { name: name ?? null, announcedAt: at },
        },
      }));
      break;
    }
    case BINGO_EVENTS.WINNER_DISMISSED:
      set({ status: GameStatus.PAUSED });
      break;
    case BINGO_EVENTS.MANUAL_MARK:
    case BINGO_EVENTS.MANUAL_UNMARK: {
      const v = event.payload.value;
      set((s) => {
        const has = s.manualMarks.includes(v);
        const next = has
          ? s.manualMarks.filter((n) => n !== v)
          : [...s.manualMarks, v].sort((a, b) => a - b);
        return { manualMarks: next };
      });
      break;
    }
    case BINGO_EVENTS.END_GAME:
      set({ status: GameStatus.GAME_SUMMARY });
      break;
    case BINGO_EVENTS.GAME_SUMMARY_DISMISSED:
      set({
        status: GameStatus.IDLE,
        sessionId: null,
        mongoId: null,
        called: [],
        drawSequence: [],
        drawIndex: 0,
        undoDepth: 0,
        manualMarks: [],
      });
      break;
    case BINGO_EVENTS.STATE_SNAPSHOT: {
      // Host answered our hydrate request with a full state dump.
      const snap = event.payload;
      set({
        sessionId: snap.sessionId ?? null,
        status: snap.status ?? GameStatus.IDLE,
        stage: snap.stage ?? Stage.ONE_LINE,
        drawSequence: [],
        drawIndex: snap.drawIndex ?? 0,
        called: snap.called ?? [],
        undoDepth: snap.undoDepth ?? 0,
        manualMarks: snap.manualMarks ?? [],
        mode: snap.mode ?? "manual",
        autoInterval: snap.autoInterval ?? 5,
        winners: snap.winners ?? {
          one_line: { name: null, announcedAt: null },
          two_lines: { name: null, announcedAt: null },
          full_house: { name: null, announcedAt: null },
        },
      });
      break;
    }
    // ---------- Bingo Check · Heartbeat Verification ----------
    case BINGO_EVENTS.BINGO_CHECK_STARTED: {
      const numbers = Array.isArray(event.payload.numbers)
        ? event.payload.numbers.slice(0, 15)
        : [];
      set({
        bingoCheck: {
          numbers,
          verifiedCount: 0,
          playerLabel: event.payload.playerLabel || "Claimed Line",
          bpm: 60,
        },
      });
      break;
    }
    case BINGO_EVENTS.BINGO_CHECK_VERIFIED: {
      set((s) =>
        s.bingoCheck
          ? {
              bingoCheck: {
                ...s.bingoCheck,
                verifiedCount:
                  event.payload.verifiedCount ?? s.bingoCheck.verifiedCount,
                bpm: event.payload.bpm ?? s.bingoCheck.bpm,
              },
            }
          : s,
      );
      break;
    }
    case BINGO_EVENTS.BINGO_CHECK_CANCELLED: {
      set({ bingoCheck: null });
      break;
    }
    case BINGO_EVENTS.BINGO_CHECK_COMPLETED: {
      // Heartbeat is stopped by the overlay reacting to verifiedCount
      // hitting total. The Host's `verifyNextBingoCheck` will then
      // publish CONFIRMED_BINGO_STARTED after a short silence beat.
      // We just clear the verification card once the celebration
      // takes over (see CONFIRMED_BINGO_STARTED below).
      break;
    }
    case BINGO_EVENTS.CONFIRMED_BINGO_STARTED: {
      const payload = event.payload || {};
      // Clear any lingering bingoCheck state and open the celebration.
      set({
        bingoCheck: null,
        confirmedBingo: {
          name: payload.name || "",
          stageLabel: payload.stageLabel || "WINNER",
          ts: payload.ts || Date.now(),
        },
      });
      break;
    }
    case BINGO_EVENTS.CONFIRMED_BINGO_DISMISSED: {
      set({ confirmedBingo: null });
      break;
    }
    default:
      break;
  }
}

export function attachProjectorReceiver() {
  const unsub = sync.subscribe(handle);
  // Ask the Host for a current snapshot in case we opened mid-game.
  sync.publish(makeEvent(BINGO_EVENTS.STATE_REQUEST, {}, "bingo"));
  return () => {
    unsub && unsub();
    if (transitionTimer) {
      clearTimeout(transitionTimer);
      transitionTimer = null;
    }
  };
}
